'use client'

/**
 * The written reason, and the one rule every interaction that takes one shares.
 *
 * Three interactions demand a reason before they commit: returning a gate slot
 * (30-R107), deciding a screening finding in either direction (30-R123), and any
 * override (30-R114). All three are enforced here rather than three times, because a
 * fourth interaction that forgot the rule is exactly the kind of drift that turns a
 * governance requirement into a preference.
 *
 * There is no textarea in `frontend/src/components/ui/`. The base asset's own oversized
 * detail page uses a bare `<textarea>` with token classes at line 1960, so this follows
 * that precedent rather than adding a component another stream may also be adding. The
 * inherited route's slug identifier is deliberately not written here: 30-R035 keeps base
 * asset field names out of the federal tree, and a citation is not a reason to carry one.
 */
import * as React from 'react'

import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'

/**
 * Whitespace is not a reason. 30-R107 and 30-R123 both require the commit control to
 * stay disabled against a whitespace-only value, and this is the single place that
 * decides it.
 */
export function isReasonSufficient(reason: string): boolean {
  return reason.trim().length > 0
}

export interface ReasonFieldProps {
  id: string
  label: string
  /** One sentence stating what the reason will be used for and where it will appear. */
  help?: string
  value: string
  onChange: (value: string) => void
  disabled?: boolean
  rows?: number
}

export function ReasonField({
  id,
  label,
  help,
  value,
  onChange,
  disabled,
  rows = 3,
}: ReasonFieldProps) {
  const helpId = `${id}-help`
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-micro uppercase text-brand-muted">
        {label}
      </Label>
      {help ? (
        <p id={helpId} className="text-sm text-muted-foreground">
          {help}
        </p>
      ) : null}
      <textarea
        id={id}
        rows={rows}
        value={value}
        disabled={disabled}
        aria-describedby={help ? helpId : undefined}
        aria-required="true"
        onChange={(event) => onChange(event.target.value)}
        className={cn(
          'focus-ring w-full resize-y rounded-xl border border-border bg-white',
          'px-3 py-2 text-sm text-foreground placeholder:text-brand-muted',
          'transition-colors duration-150',
          'disabled:cursor-not-allowed disabled:opacity-50'
        )}
      />
    </div>
  )
}
